import { TranslocoPipe, provideTranslocoScope, translate } from '@jsverse/transloco';
import { DatePipe, Location } from '@angular/common';
import { TranslocoCurrencyPipe } from '@jsverse/transloco-locale';
import { Component, inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { CanDirective, CapabilityNames } from 'core';
import { LoadingSpinner } from 'design-system';
import { finalize } from 'rxjs';
import { Worksheet, WorksheetSearch } from '../../interfaces/worksheet.model';
import { WorksheetsService } from '../../services/worksheets.service';
import { DepositsService } from '../../services/deposits.service';
import { toDate } from '../../utils/receipt.utils';

interface WorksheetRow {
  id: string;
  numeroBrogliaccio: number;
  data: Date | null;
  numeroIncassi: number;
  importoContanti: number;
  importoAssegni: number;
  importoTotale: number;
  selezionato: boolean;
}

@Component({
  providers: [provideTranslocoScope('receipts')],
  selector: 'rev-receipts-distinte-new',
  imports: [TranslocoPipe,
    CanDirective,
    TranslocoCurrencyPipe,
    DatePipe,
    LoadingSpinner,
    MatButtonModule,
    MatCheckboxModule,
    MatTableModule,
  ],
  templateUrl: './receipts-distinte-new.html',
  styleUrl: './receipts-distinte-new.scss',
})
export class ReceiptsDistinteNew implements OnInit {
  private readonly worksheetsService = inject(WorksheetsService);
  private readonly depositsService = inject(DepositsService);
  private readonly snackBar = inject(MatSnackBar);

  constructor(private location: Location) {}

  readonly CAPS = CapabilityNames;

  brogliacciAssociabili: WorksheetRow[] = [];
  isLoading = false;
  isSaving = false;

  readonly colonneTabella = [
    'numeroBrogliaccio',
    'data',
    'numeroIncassi',
    'importoContanti',
    'importoAssegni',
    'importoTotale',
    'selezionato',
  ];

  ngOnInit(): void {
    this.loadBrogliacci();
  }

  // soloAssociabili: Confermati e non gia' dentro un'altra distinta.
  loadBrogliacci(): void {
    this.isLoading = true;

    const filter: WorksheetSearch = {
      searchObject: {
        dataDa: null,
        dataA: null,
        statiBrogliaccio: [],
        soloAssociabili: true,
      },
    };

    this.worksheetsService
      .getWorksheetList(1, 0, filter)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: data => {
          this.brogliacciAssociabili = (data.items ?? []).map(worksheet => this.toRow(worksheet));
        },
        error: err => {
          console.error('Errore caricamento brogliacci associabili', err);
          this.brogliacciAssociabili = [];
        },
      });
  }

  get tuttiInclusi(): boolean {
    return this.brogliacciAssociabili.length > 0
      && this.brogliacciAssociabili.every(brogliaccio => brogliaccio.selezionato);
  }

  get selezioneParziale(): boolean {
    return !this.tuttiInclusi
      && this.brogliacciAssociabili.some(brogliaccio => brogliaccio.selezionato);
  }

  get almenoUnBrogliaccioIncluso(): boolean {
    return this.brogliacciAssociabili.some(brogliaccio => brogliaccio.selezionato);
  }

  get totaleContantiSelezionati(): number {
    return this.calcolaTotaleSelezionato('importoContanti');
  }

  get totaleAssegniSelezionati(): number {
    return this.calcolaTotaleSelezionato('importoAssegni');
  }

  get totaleBrogliacciSelezionati(): number {
    return this.calcolaTotaleSelezionato('importoTotale');
  }

  impostaTuttiInclusi(inclusi: boolean): void {
    this.brogliacciAssociabili.forEach(brogliaccio => {
      brogliaccio.selezionato = inclusi;
    });
  }

  impostaBrogliaccioIncluso(id: string, incluso: boolean): void {
    const brogliaccio = this.brogliacciAssociabili.find(item => item.id === id);

    if (brogliaccio) {
      brogliaccio.selezionato = incluso;
    }
  }

  // I brogliacci passano a PresaInCarico e la distinta nasce in Lavorazione.
  // Zona e banca non si mandano: la zona la deriva il backend, la banca si
  // sceglie alla chiusura.
  creaDistinta(): void {
    if (!this.almenoUnBrogliaccioIncluso || this.isSaving) {
      return;
    }

    const idBrogliacci = this.brogliacciAssociabili
      .filter(brogliaccio => brogliaccio.selezionato)
      .map(brogliaccio => brogliaccio.id);

    this.isSaving = true;

    this.depositsService
      .createDeposit(idBrogliacci)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: () => this.tornaIndietro(),
        error: err => {
          console.error('Errore creazione distinta', err);
          this.snackBar.open(err?.error?.detail ?? translate<string>('receipts.ui.creazioneDellaDistintaNonRiuscita'), translate<string>('receipts.ui.chiudi'), {
            duration: 5000,
          });
        },
      });
  }

  tornaIndietro(): void {
    this.location.back();
  }

  private toRow(worksheet: Worksheet): WorksheetRow {
    const contanti = worksheet.totaleValuta ?? 0;
    const assegni = worksheet.totaleAssegni ?? 0;

    return {
      id: worksheet.id,
      numeroBrogliaccio: worksheet.numeroBrogliaccio,
      data: toDate(worksheet.data),
      numeroIncassi: worksheet.numeroIncassi,
      importoContanti: contanti,
      importoAssegni: assegni,
      importoTotale: worksheet.totale ?? contanti + assegni,
      selezionato: false,
    };
  }

  private calcolaTotaleSelezionato(
    campo: 'importoContanti' | 'importoAssegni' | 'importoTotale',
  ): number {
    return this.brogliacciAssociabili
      .filter(brogliaccio => brogliaccio.selezionato)
      .reduce((totale, brogliaccio) => totale + brogliaccio[campo], 0);
  }
}
